import { currentRegistration, registerAppServiceWorker, swSupported } from '@/push/serviceWorker';
import {
  FLUSH_SALES_MESSAGE,
  NETWORK_FAILED_MESSAGE,
  NETWORK_OK_MESSAGE,
  SALES_SYNC_TAG,
  SKIP_WAITING_MESSAGE,
  isSwMessage,
} from './messages';
import { pwaStore } from './store';

/**
 * Page-side PWA bootstrap (task.md §4.14), called once from main.tsx:
 * - mirrors the connection into the store: navigator.onLine at start, then the 'online'/'offline' events and the
 *   worker's NETWORK_FAILED / NETWORK_OK messages (navigator.onLine is not reliable everywhere) → OfflineBanner;
 * - registers src/sw.ts (production build or VITE_ENABLE_SW=true — see push/serviceWorker.ts) so the app
 *   shell is precached before the first login, not only after the push subscription;
 * - flags `updateReady` when a NEW worker takes over a page that already had one (the worker calls
 *   skipWaiting, so the swap happens without a prompt; the running code is stale until a reload);
 * - forwards the worker's FLUSH_SALES message (Background Sync) to the registered flush listeners.
 */

type FlushListener = () => void;
const flushListeners = new Set<FlushListener>();

export function onFlushRequest(listener: FlushListener): () => void {
  flushListeners.add(listener);
  return () => {
    flushListeners.delete(listener);
  };
}

export function requestFlush(): void {
  for (const listener of [...flushListeners]) listener();
}

export interface PwaWindow {
  addEventListener(type: 'online' | 'offline', listener: () => void): void;
  removeEventListener(type: 'online' | 'offline', listener: () => void): void;
}

export interface SwContainerLike {
  controller: unknown;
  addEventListener(type: 'controllerchange' | 'message', listener: (event: { data?: unknown }) => void): void;
  removeEventListener(
    type: 'controllerchange' | 'message',
    listener: (event: { data?: unknown }) => void,
  ): void;
}

export interface StartPwaDeps {
  win: PwaWindow;
  nav: { onLine: boolean };
  container: SwContainerLike | null;
  register: () => Promise<unknown>;
  /** Calls `check` now and then; returns the teardown. Defaults to watchForUpdates. */
  watch?: (check: () => void) => () => void;
}

/** How often an open page asks the server whether a new release is out. */
export const UPDATE_CHECK_MS = 15 * 60 * 1000;

/**
 * NEW — a browser looks for a new service worker only when a page is loaded, so a till left open for
 * days kept running the release it was opened with and never showed the "update ready" banner. Ask
 * every UPDATE_CHECK_MS, and whenever the page comes back into view.
 */
export function watchForUpdates(check: () => void): () => void {
  const timer = window.setInterval(check, UPDATE_CHECK_MS);
  const onVisible = () => {
    if (document.visibilityState === 'visible') check();
  };
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
  };
}

function defaultDeps(): StartPwaDeps {
  return {
    win: window,
    nav: navigator,
    container: swSupported() ? navigator.serviceWorker : null,
    register: registerAppServiceWorker,
    watch: watchForUpdates,
  };
}

/** Wires the listeners above; returns the teardown (tests). */
export function startPwa(deps: StartPwaDeps = defaultDeps()): () => void {
  pwaStore.update({ online: deps.nav.onLine });
  const wentOnline = () => pwaStore.update({ online: true });
  const wentOffline = () => pwaStore.update({ online: false });
  deps.win.addEventListener('online', wentOnline);
  deps.win.addEventListener('offline', wentOffline);

  const stops: (() => void)[] = [
    () => {
      deps.win.removeEventListener('online', wentOnline);
      deps.win.removeEventListener('offline', wentOffline);
    },
  ];

  const { container } = deps;
  if (container) {
    const hadController = container.controller !== null && container.controller !== undefined;
    const onControllerChange = () => {
      if (hadController) pwaStore.update({ updateReady: true });
    };
    const onMessage = (event: { data?: unknown }) => {
      if (!isSwMessage(event.data)) return;
      if (event.data.type === FLUSH_SALES_MESSAGE) requestFlush();
      else if (event.data.type === NETWORK_FAILED_MESSAGE) wentOffline();
      else if (event.data.type === NETWORK_OK_MESSAGE) wentOnline();
    };
    container.addEventListener('controllerchange', onControllerChange);
    container.addEventListener('message', onMessage);
    stops.push(() => {
      container.removeEventListener('controllerchange', onControllerChange);
      container.removeEventListener('message', onMessage);
    });
    let stopped = false;
    void deps
      .register()
      .then((registration) => {
        const update = (registration as { update?: () => Promise<unknown> } | null)?.update;
        if (stopped || !deps.watch || typeof update !== 'function') return;
        // a found release installs, takes over (skipWaiting) and fires controllerchange → the banner
        const stopWatching = deps.watch(() => {
          void update.call(registration).catch(() => undefined);
        });
        stops.push(stopWatching);
      })
      .catch(() => {
        // registration failure (e.g. HTTP without localhost): the app keeps working online
      });
    stops.push(() => {
      stopped = true;
    });
  }

  return () => {
    for (const stop of stops) stop();
  };
}

/**
 * Asks the worker for a Background Sync (`SALES_SYNC_TAG`) so the queue is flushed even if the page is in the
 * background when the connection returns. Returns false when the API is unavailable (Safari/Firefox, dev).
 */
interface BackgroundSyncRegistration {
  /** Background Sync API — Chromium only (Safari/Firefox and jsdom have no `sync`). */
  sync?: { register: (tag: string) => Promise<void> };
}

export async function requestSalesSync(): Promise<boolean> {
  const registration = (await currentRegistration()) as BackgroundSyncRegistration | null;
  const sync = registration?.sync;
  if (!sync) return false;
  try {
    await sync.register(SALES_SYNC_TAG);
    return true;
  } catch {
    return false;
  }
}

/** Update prompt "Rifresko": activate a waiting worker (if any) and reload. */
export async function applyUpdate(reload: () => void = () => window.location.reload()): Promise<void> {
  const registration = await currentRegistration();
  registration?.waiting?.postMessage({ type: SKIP_WAITING_MESSAGE });
  reload();
}
